import { useEffect, useMemo, useRef, useState } from 'react';
import { api, prepareImageUpload } from '../api.js';
import { supplyForecast } from '../utils.js';
import { useConfig } from '../config.js';
import { resolveSupplyReorderUrl } from '../reorder.js';
import {
  storeAndLink,
  cleanStoreName,
  retailerForStore,
  STORE_NAME_MAX,
} from '../../../shared/reorder-link.js';
import { getDisplayUnits } from '../units.js';
import { saltSupplyYield } from '../salt-mix.js';
import { useConfirm } from './ConfirmDialog.jsx';

export function SuppliesView({ tank, user, onReload }) {
  const reorderLibrary = useConfig()?.reorder;
  const [showAdd, setShowAdd] = useState(false);
  // Inline edit: the PATCH endpoint always supported name/unit/size/
  // daily_use — the UI just never offered anything past +1/−1/Refill
  // (user-reported "where do I edit supplies?").
  const [editingId, setEditingId] = useState(null);
  // One visible action per card (owner, 2026-10-21): −1 and Refill, with
  // Reorder once it runs low. A −1 offers Undo for a few seconds; any other
  // count is Remaining under Edit.
  const [undoId, setUndoId] = useState(null);
  const undoTimer = useRef(null);
  useEffect(() => () => clearTimeout(undoTimer.current), []);
  const confirm = useConfirm();
  const [editDraft, setEditDraft] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState('');
  const sorted = useMemo(
    () =>
      (tank.supplies || [])
        .map((s) => ({ ...s, f: supplyForecast(s) }))
        .sort((a, b) => (a.f.days || 1000) - (b.f.days || 1000)),
    [tank.supplies]
  );

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditDraft({
      name: s.name || '',
      unit: s.unit || '',
      size: s.size != null ? String(s.size) : '',
      remaining: s.remaining != null ? String(s.remaining) : '',
      daily_use: s.daily_use != null ? String(s.daily_use) : '',
      reorder_url: s.reorder_url || '',
      store_name: s.store_name || '',
    });
    setEditErr('');
  };
  const saveEdit = async (s) => {
    if (!editDraft?.name?.trim()) return;
    // Said here, before the request: a link the server would refuse used to
    // fail in silence and keep nothing (feedback #65). An untouched link is
    // not checked again, so an older one never blocks a Remaining edit.
    const linkChanged = editDraft.reorder_url.trim() !== (s.reorder_url || '');
    const where = storeAndLink(editDraft.store_name, linkChanged ? editDraft.reorder_url : '');
    if (where.error) {
      setEditErr(where.error);
      return;
    }
    setEditBusy(true);
    setEditErr('');
    try {
      await api(`/api/tanks/${tank.id}/supplies/${s.id}`, {
        method: 'PATCH',
        body: {
          name: editDraft.name.trim().slice(0, 100),
          unit: editDraft.unit.trim().slice(0, 20) || null,
          size: editDraft.size === '' ? null : Number(editDraft.size),
          remaining: editDraft.remaining === '' ? null : Number(editDraft.remaining),
          daily_use: editDraft.daily_use === '' ? 0 : Number(editDraft.daily_use),
          ...(linkChanged ? { reorder_url: where.reorder_url } : {}),
          store_name: where.store_name,
        },
      });
      await onReload();
      setEditingId(null);
      setEditDraft(null);
    } catch (e) {
      setEditErr(e.message || 'Could not save. Try again.');
    } finally {
      setEditBusy(false);
    }
  };

  // Quantity taps go through /adjust so every movement lands in the
  // supply ledger; PATCH stays for spec edits only.
  const adjust = async (s, delta) => {
    await api(`/api/tanks/${tank.id}/supplies/${s.id}/adjust`, {
      method: 'POST',
      body: { delta },
    });
    await onReload();
  };
  // Removing a bottle takes its history with it, so it asks first.
  const remove = async (s) => {
    const ok = await confirm({
      title: `Remove ${s.name}?`,
      body: 'Its amount and its history go too. You can add it again from Supplies.',
      confirmLabel: 'Remove',
      cancelLabel: 'Keep it',
      destructive: true,
    });
    if (!ok) return;
    await api(`/api/tanks/${tank.id}/supplies/${s.id}`, { method: 'DELETE' });
    setEditingId(null);
    await onReload();
  };
  const refill = async (s) => {
    if (s.size) {
      await api(`/api/tanks/${tank.id}/supplies/${s.id}/adjust`, {
        method: 'POST',
        body: { refill: true },
      });
      await onReload();
    }
  };
  // Reorder link: the keeper's own pasted URL wins; otherwise a
  // retailer search for the product name (their preferred retailer,
  // else the first in the library) — so the button works with zero
  // setup. The click itself is the signal (what gets reordered, when)
  // so beacon it best-effort first.
  // A store name the library knows ("Bulk Reef Supply") points the search
  // at that store; a local store keeps the keeper's preferred retailer.
  const storeRetailer = (s) => retailerForStore(s.store_name, reorderLibrary?.retailers);
  const reorderUrlFor = (s) =>
    resolveSupplyReorderUrl({
      reorderUrl: s.reorder_url,
      name: s.name,
      library: reorderLibrary,
      preferredRetailer: storeRetailer(s)?.k || user?.preferred_retailer,
    });
  const reorder = (s) => {
    const url = reorderUrlFor(s);
    if (!url) return;
    api(`/api/tanks/${tank.id}/supplies/${s.id}/reorder-click`, { method: 'POST' }).catch(() => {});
    window.open(url, '_blank', 'noopener');
  };

  return (
    <div style={{ padding: '0 20px 40px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <h2 className="font-heading" style={{ fontSize: 15, letterSpacing: '0.08em' }}>
            SUPPLIES
          </h2>
          <p
            className="font-heading"
            style={{
              fontSize: 12,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--cyan)',
              marginTop: 2,
            }}
          >
            Never run out at the wrong moment
          </p>
        </div>
        <button
          onClick={() => setShowAdd((v) => !v)}
          className="font-heading"
          style={{
            fontSize: 12,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            padding: '6px 12px',
            borderRadius: 999,
            color: 'var(--cyan)',
            border: '1px solid var(--accent-border)',
          }}
        >
          {showAdd ? 'Close' : '+ Add'}
        </button>
      </div>
      {showAdd && (
        <AddSupplyForm
          tank={tank}
          onDone={async () => {
            await onReload();
            setShowAdd(false);
          }}
        />
      )}
      {sorted.length === 0 && !showAdd && (
        <div
          style={{
            padding: 20,
            textAlign: 'center',
            borderRadius: 12,
            background: 'var(--surface)',
            border: '1px solid var(--surface-border)',
            fontSize: 15,
            color: 'var(--text-muted)',
          }}
        >
          No supplies tracked yet. Tap + Add to start.
        </div>
      )}
      {sorted.map((s) => {
        const f = s.f;
        // Commercial hygiene: the need is identified first, the action
        // offered second (PRODUCT-PRINCIPLES section 5). Reorder only
        // appears once the runway says the supply is actually low.
        const showReorder = (f.status === 'low' || f.status === 'critical') && reorderUrlFor(s);
        const color =
          f.status === 'critical'
            ? 'var(--alert)'
            : f.status === 'low'
              ? 'var(--cyan-dim)'
              : 'var(--cyan)';
        const pct = s.size > 0 ? Math.min(1, (s.remaining || 0) / s.size) : 0;
        return (
          <div
            key={s.id}
            style={{
              padding: 14,
              borderRadius: 14,
              background: 'var(--surface)',
              border: `1px solid ${f.status === 'critical' ? 'var(--alert-border)' : 'var(--surface-border)'}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 8,
                marginBottom: 8,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  className="font-heading"
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: 'var(--text-dim)',
                  }}
                >
                  {s.category || 'supply'}
                </div>
                <div
                  style={{
                    fontSize: 16,
                    color: 'var(--text)',
                    marginTop: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontWeight: 500,
                  }}
                >
                  {s.name}
                </div>
                {s.store_name && (
                  <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 2 }}>
                    From {s.store_name}
                  </div>
                )}
              </div>
              <div
                className="font-mono"
                style={{ fontSize: 14, color, fontWeight: 500, textAlign: 'right' }}
              >
                {f.label}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  background: 'var(--surface-border)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${(pct * 100).toFixed(1)}%`,
                    background: color,
                    transition: 'width 0.4s ease',
                  }}
                />
              </div>
              <span
                className="font-mono"
                style={{ fontSize: 12, color: 'var(--text-dim)', minWidth: 80, textAlign: 'right' }}
              >
                {s.remaining ?? 0}
                {s.size ? `/${s.size}` : ''} {s.unit || ''}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              <button
                onClick={async () => {
                  await adjust(s, -1);
                  setUndoId(s.id);
                  clearTimeout(undoTimer.current);
                  undoTimer.current = setTimeout(() => setUndoId(null), 8000);
                }}
                aria-label={`Used one ${s.unit || ''}`.trim()}
                className="font-heading"
                style={{
                  padding: '6px 12px',
                  minHeight: 44,
                  minWidth: 44,
                  borderRadius: 999,
                  border: '1px solid var(--surface-border)',
                  color: 'var(--text-muted)',
                  fontSize: 13,
                  letterSpacing: '0.1em',
                }}
              >
                −1
              </button>
              {undoId === s.id && (
                <button
                  onClick={() => {
                    setUndoId(null);
                    adjust(s, +1);
                  }}
                  className="font-heading"
                  style={{
                    padding: '6px 12px',
                    minHeight: 44,
                    borderRadius: 999,
                    border: '1px solid var(--surface-border)',
                    color: 'var(--cyan)',
                    fontSize: 12,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                  }}
                >
                  Undo
                </button>
              )}
              {s.size && (
                <button
                  onClick={() => refill(s)}
                  className="font-heading"
                  style={{
                    padding: '6px 12px',
                    minHeight: 44,
                    borderRadius: 999,
                    background: color,
                    color: 'var(--cyan-text)',
                    fontSize: 12,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                  }}
                >
                  Refill
                </button>
              )}
              {showReorder && (
                <button
                  onClick={() => reorder(s)}
                  className="font-heading"
                  title={
                    s.reorder_url
                      ? 'Opens your saved link'
                      : storeRetailer(s)
                        ? `Searches ${storeRetailer(s).label} for this`
                        : 'Searches your retailer for this'
                  }
                  style={{
                    padding: '6px 12px',
                    minHeight: 44,
                    borderRadius: 999,
                    border: '1px solid var(--accent-border)',
                    color: 'var(--cyan)',
                    fontSize: 12,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                  }}
                >
                  Reorder
                </button>
              )}
              <button
                onClick={() => (editingId === s.id ? setEditingId(null) : startEdit(s))}
                className="font-heading"
                style={{
                  padding: '6px 12px',
                  minHeight: 44,
                  borderRadius: 999,
                  border: '1px solid var(--surface-border)',
                  color: editingId === s.id ? 'var(--cyan)' : 'var(--text-muted)',
                  fontSize: 12,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  marginLeft: 'auto',
                }}
              >
                {editingId === s.id ? 'Close' : 'Edit'}
              </button>
            </div>
            {editingId === s.id && editDraft && (
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 10,
                  borderTop: '1px dashed var(--surface-border)',
                  display: 'grid',
                  // Wraps on a phone: five columns at 390 px ran the labels together.
                  gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                  gap: 8,
                  alignItems: 'end',
                }}
              >
                {[
                  ['name', 'Name', 'text'],
                  ['unit', 'Unit', 'text'],
                  ['size', 'Package size', 'number'],
                  ['remaining', 'Remaining', 'number'],
                  ['daily_use', 'Daily use (auto-learned)', 'number'],
                ].map(([field, label, type]) => (
                  <label
                    key={field}
                    style={{ minWidth: 0, gridColumn: field === 'name' ? '1 / -1' : undefined }}
                  >
                    <span
                      className="font-heading"
                      style={{
                        display: 'block',
                        fontSize: 10,
                        letterSpacing: '0.2em',
                        textTransform: 'uppercase',
                        color: 'var(--text-dim)',
                        marginBottom: 3,
                      }}
                    >
                      {label}
                    </span>
                    <input
                      type={type}
                      inputMode={type === 'number' ? 'decimal' : undefined}
                      value={editDraft[field]}
                      onChange={(e) => setEditDraft((d) => ({ ...d, [field]: e.target.value }))}
                      style={{
                        width: '100%',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: '1px solid var(--surface-border)',
                        outline: 'none',
                        padding: '4px 0',
                        fontSize: 18,
                        color: 'var(--text)',
                      }}
                    />
                  </label>
                ))}
                <label style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                  <span
                    className="font-heading"
                    style={{
                      display: 'block',
                      fontSize: 10,
                      letterSpacing: '0.2em',
                      textTransform: 'uppercase',
                      color: 'var(--text-dim)',
                      marginBottom: 3,
                    }}
                  >
                    Where you buy it
                  </span>
                  <input
                    type="text"
                    placeholder="Store name, like Bulk Reef Supply or your local store"
                    maxLength={STORE_NAME_MAX}
                    value={editDraft.store_name}
                    onChange={(e) => setEditDraft((d) => ({ ...d, store_name: e.target.value }))}
                    style={{
                      width: '100%',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--surface-border)',
                      outline: 'none',
                      padding: '4px 0',
                      // 16 px: iOS Safari zooms the page into anything smaller.
                      fontSize: 18,
                      color: 'var(--text)',
                    }}
                  />
                </label>
                <label style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                  <span
                    className="font-heading"
                    style={{
                      display: 'block',
                      fontSize: 10,
                      letterSpacing: '0.2em',
                      textTransform: 'uppercase',
                      color: 'var(--text-dim)',
                      marginBottom: 3,
                    }}
                  >
                    Product link (optional)
                  </span>
                  <input
                    type="url"
                    inputMode="url"
                    placeholder="bulkreefsupply.com/…"
                    value={editDraft.reorder_url}
                    onChange={(e) => {
                      setEditErr('');
                      setEditDraft((d) => ({ ...d, reorder_url: e.target.value }));
                    }}
                    style={{
                      width: '100%',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--surface-border)',
                      outline: 'none',
                      padding: '4px 0',
                      // 16 px: iOS Safari zooms the page into anything smaller.
                      fontSize: 18,
                      color: 'var(--text)',
                    }}
                  />
                </label>
                {editErr && (
                  <div
                    role="alert"
                    style={{ gridColumn: '1 / -1', fontSize: 14, color: 'var(--alert)' }}
                  >
                    {editErr}
                  </div>
                )}
                <button
                  onClick={() => saveEdit(s)}
                  disabled={editBusy || !editDraft.name.trim()}
                  className="font-heading"
                  style={{
                    gridColumn: '1 / -1',
                    padding: '9px 0',
                    minHeight: 44,
                    borderRadius: 10,
                    background: 'var(--cyan)',
                    color: 'var(--cyan-text)',
                    fontWeight: 600,
                    fontSize: 12,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    opacity: editBusy || !editDraft.name.trim() ? 0.5 : 1,
                  }}
                >
                  {editBusy ? 'Saving…' : 'Save changes'}
                </button>
                {/* Behind Edit, one action per card (owner, 2026-10-21). */}
                <button
                  type="button"
                  onClick={() => remove(s)}
                  className="font-heading"
                  style={{
                    gridColumn: '1 / -1',
                    minHeight: 44,
                    borderRadius: 10,
                    border: '1px solid var(--surface-border)',
                    color: 'var(--text-dim)',
                    fontSize: 12,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                  }}
                >
                  Remove this supply
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AddSupplyForm({ tank, onDone }) {
  const config = useConfig();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('alkalinity');
  const [unit, setUnit] = useState('mL');
  const [size, setSize] = useState('');
  const [storeName, setStoreName] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  // AI spec-fill: type a product and let Sensei recall the retail
  // package specs, or photograph the label and let Vision read them.
  // Always a prefill — every field stays editable, and the status line
  // says to check the label so a wrong size never lands silently.
  const [aiBusy, setAiBusy] = useState(null); // null | 'lookup' | 'label'
  const [aiMsg, setAiMsg] = useState('');
  const [aiErr, setAiErr] = useState('');
  const labelRef = useRef(null);

  const applySpec = (spec) => {
    if (!spec) return;
    if (spec.name) setName(spec.name);
    if (spec.category) setCategory(spec.category);
    if (spec.unit) setUnit(spec.unit);
    if (spec.size != null) setSize(String(spec.size));
    const filled = spec.size != null || spec.unit || spec.name;
    if (!filled || spec.confidence < 0.5) {
      setAiMsg(spec.note || 'Sensei is not sure about this one — fill what you know.');
    } else {
      setAiMsg('Prefilled by Sensei. Check it against the label before saving.');
    }
  };

  const lookupSpecs = async () => {
    if (!name.trim() || aiBusy) return;
    setAiBusy('lookup');
    setAiErr('');
    setAiMsg('');
    try {
      const spec = await api(`/api/tanks/${tank.id}/supplies/spec-lookup`, {
        method: 'POST',
        body: { query: name.trim() },
      });
      applySpec(spec);
    } catch (e) {
      setAiErr(e.message || 'Sensei could not look that up right now.');
    } finally {
      setAiBusy(null);
    }
  };

  const readLabel = async (file) => {
    if (!file || aiBusy) return;
    setAiBusy('label');
    setAiErr('');
    setAiMsg('');
    try {
      const { body, mime } = await prepareImageUpload(file);
      const res = await fetch(`/api/tanks/${tank.id}/supplies/label-read`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': mime },
        body,
      });
      const spec = await res.json().catch(() => null);
      if (!res.ok) throw new Error(spec?.error || `Could not read the label (${res.status}).`);
      applySpec(spec);
    } catch (e) {
      setAiErr(e.message || 'Sensei could not read that label.');
    } finally {
      setAiBusy(null);
    }
  };

  // Supplies are picked, not typed: every category offers common market
  // products with their retail specs (admin-editable supply_presets), so
  // nobody looks up "how big is the ESV gallon" online. Picking one
  // prefills name/unit/size — all still editable. Salt has its own
  // richer flow (brand + package from salt_mixes, tied to the
  // water-change mixing guide).
  const presetCfg = config?.supply_presets?.categories?.[category] || null;
  const presets = presetCfg?.presets || [];
  const [presetIdx, setPresetIdx] = useState('');
  const pickCategory = (c) => {
    setCategory(c);
    setPresetIdx('');
    const def = config?.supply_presets?.categories?.[c]?.unit_default;
    if (def != null) setUnit(def);
  };
  const pickPreset = (idx) => {
    setPresetIdx(idx);
    const pre = presets[Number(idx)];
    if (pre) {
      setName(pre.label);
      if (pre.unit) setUnit(pre.unit);
      if (pre.size != null) setSize(String(pre.size));
    }
  };

  const saltBrands = config?.salt_mixes?.brands || [];
  const [brandKey, setBrandKey] = useState('');
  const [packageIdx, setPackageIdx] = useState(0);
  const brand = saltBrands.find((b) => b.key === brandKey) || null;
  const packages = brand?.packages || [];
  // Custom mode is REAL when the brand has no packages at all (a stale
  // stored catalog, or an admin-trimmed brand): the select can only
  // display "Custom size…" then, so the form must actually BE in custom
  // mode or the gallons input never renders and Add stays disabled
  // (user-reported).
  const isCustomPackage = packageIdx === 'custom' || packages.length === 0;
  const pickedPackage = isCustomPackage ? null : packages[Number(packageIdx)] || null;
  const isSalt = category === 'salt';

  const pickBrand = (key) => {
    setBrandKey(key);
    setPackageIdx(0);
    const b = saltBrands.find((x) => x.key === key);
    const pkg = b?.packages?.[0];
    if (b) setName(pkg ? `${b.label} — ${pkg.label}` : b.label);
  };
  const pickPackage = (idx) => {
    setPackageIdx(idx);
    if (brand && idx !== 'custom') {
      const pkg = packages[Number(idx)];
      if (pkg) setName(`${brand.label} — ${pkg.label}`);
    }
  };

  const litres = getDisplayUnits(tank).volume === 'L';
  // A package is listed by the gallons it mixes; a custom size is typed in
  // the tank's own unit.
  const saltYield = pickedPackage
    ? saltSupplyYield(pickedPackage.gal, litres ? 'L' : 'gal')
    : Number(size) > 0
      ? { unit: litres ? 'L' : 'gal', size: Number(size) }
      : null;
  const saltSize = saltYield?.size ?? null;
  const canSave = isSalt ? !!brandKey && !!saltSize : !!name.trim();

  const save = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setBusy(true);
    try {
      const body = isSalt
        ? {
            name: (name.trim() || brand?.label || 'Salt mix').slice(0, 100),
            category,
            unit: saltYield.unit,
            size: saltSize,
            remaining: saltSize,
            daily_use: 0,
            reorder_at: 14,
            store_name: cleanStoreName(storeName),
          }
        : {
            name: name.trim(),
            category,
            unit,
            size: size ? Number(size) : null,
            remaining: size ? Number(size) : null,
            daily_use: 0,
            reorder_at: 14,
            store_name: cleanStoreName(storeName),
          };
      setSaveErr('');
      await api(`/api/tanks/${tank.id}/supplies`, { method: 'POST', body });
      onDone();
    } catch (err) {
      setSaveErr(err.message || 'Could not add it. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const box = {
    padding: 12,
    borderRadius: 12,
    background: 'var(--surface)',
    border: '1px solid var(--surface-border)',
  };
  const cats = [
    'alkalinity',
    'calcium',
    'magnesium',
    'iodine',
    'salt',
    'test-reagent',
    'icp-kit',
    'mechanical',
    'media',
    'food',
    'other',
  ];

  const labelStyle = {
    fontSize: 11,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: 'var(--text-dim)',
    marginBottom: 2,
  };
  const inputStyle = {
    width: '100%',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    padding: '4px 0',
    fontSize: 16,
    color: 'var(--text)',
  };
  const selectStyle = { ...inputStyle, appearance: 'none' };
  const optStyle = { background: 'var(--sheet-bg)', color: 'var(--text)' };

  return (
    <form
      onSubmit={save}
      className="slide-in"
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      {/* Category first — the fields below adapt to what's being added. */}
      <div style={box}>
        <div className="font-heading" style={labelStyle}>
          What is it
        </div>
        <select value={category} onChange={(e) => pickCategory(e.target.value)} style={selectStyle}>
          {cats.map((c) => (
            <option key={c} value={c} style={optStyle}>
              {c}
            </option>
          ))}
        </select>
      </div>

      {!isSalt && presets.length > 0 && (
        <div style={box}>
          <div className="font-heading" style={labelStyle}>
            Product
          </div>
          <select
            value={presetIdx}
            onChange={(e) => pickPreset(e.target.value)}
            style={selectStyle}
          >
            <option value="" style={optStyle}>
              Pick a product (prefills the specs)…
            </option>
            {presets.map((pre, i) => (
              <option key={pre.label} value={i} style={optStyle}>
                {pre.label}
              </option>
            ))}
            <option value="custom" style={optStyle}>
              Something else…
            </option>
          </select>
        </div>
      )}

      {isSalt ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div style={box}>
              <div className="font-heading" style={labelStyle}>
                Brand
              </div>
              <select
                value={brandKey}
                onChange={(e) => pickBrand(e.target.value)}
                style={selectStyle}
              >
                <option value="" style={optStyle}>
                  Pick your salt…
                </option>
                {saltBrands.map((b) => (
                  <option key={b.key} value={b.key} style={optStyle}>
                    {b.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={box}>
              <div className="font-heading" style={labelStyle}>
                Package
              </div>
              <select
                value={isCustomPackage ? 'custom' : packageIdx}
                onChange={(e) => pickPackage(e.target.value)}
                disabled={!brand}
                style={selectStyle}
              >
                {packages.map((pkg, i) => (
                  <option key={pkg.label} value={i} style={optStyle}>
                    {pkg.label}
                  </option>
                ))}
                <option value="custom" style={optStyle}>
                  Custom size…
                </option>
              </select>
            </div>
          </div>
          {isCustomPackage && (
            <div style={box}>
              <div className="font-heading" style={labelStyle}>
                Makes how many {litres ? 'litres' : 'gallons'}
              </div>
              <input
                value={size}
                onChange={(e) => setSize(e.target.value)}
                type="number"
                inputMode="decimal"
                placeholder="55"
                autoFocus
                style={{
                  ...inputStyle,
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                }}
              />
            </div>
          )}
          {brand && (
            <div style={box}>
              <div className="font-heading" style={labelStyle}>
                Name (editable)
              </div>
              <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
            </div>
          )}
          <p style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.5, margin: '0 2px' }}>
            Tracked as the {litres ? 'litres' : 'gallons'} of saltwater it can mix
            {saltSize ? ` (${saltSize} ${saltYield.unit})` : ''} — logging a water change draws it
            down automatically, and the mixing guide uses this brand.
          </p>
        </>
      ) : (
        <>
          <div style={box}>
            <div className="font-heading" style={labelStyle}>
              Name
            </div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="BRS Soda Ash"
              style={{ ...inputStyle, fontSize: 17 }}
            />
          </div>
          <input
            ref={labelRef}
            type="file"
            accept="image/*,.heic,.heif"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              readLabel(f);
            }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={lookupSpecs}
              disabled={!name.trim() || !!aiBusy}
              className="font-heading"
              style={{
                flex: 1,
                padding: '8px 0',
                borderRadius: 999,
                border: '1px solid var(--accent-border)',
                color: 'var(--cyan)',
                fontSize: 11,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                opacity: !name.trim() || aiBusy ? 0.5 : 1,
              }}
            >
              {aiBusy === 'lookup' ? 'Looking up…' : 'Sensei, fill the specs'}
            </button>
            <button
              type="button"
              onClick={() => labelRef.current?.click()}
              disabled={!!aiBusy}
              className="font-heading"
              style={{
                flex: 1,
                padding: '8px 0',
                borderRadius: 999,
                border: '1px solid var(--accent-border)',
                color: 'var(--cyan)',
                fontSize: 11,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                opacity: aiBusy ? 0.5 : 1,
              }}
            >
              {aiBusy === 'label' ? 'Reading label…' : 'Snap the label'}
            </button>
          </div>
          {aiBusy && (
            <p style={{ fontSize: 13, color: 'var(--text-dim)', margin: '0 2px' }}>
              {aiBusy === 'lookup'
                ? 'Sensei is looking up the package specs…'
                : 'Sensei is reading the label…'}
            </p>
          )}
          {aiMsg && !aiBusy && (
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 2px' }}>{aiMsg}</p>
          )}
          {aiErr && !aiBusy && (
            <p role="alert" style={{ fontSize: 13, color: 'var(--alert)', margin: '0 2px' }}>
              {aiErr}
            </p>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div style={box}>
              <div className="font-heading" style={labelStyle}>
                Unit
              </div>
              <input
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="mL"
                style={inputStyle}
              />
            </div>
            <div style={box}>
              <div className="font-heading" style={labelStyle}>
                Package size
              </div>
              <input
                value={size}
                onChange={(e) => setSize(e.target.value)}
                type="number"
                inputMode="decimal"
                placeholder="2000"
                style={{
                  ...inputStyle,
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                }}
              />
            </div>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.5, margin: '0 2px' }}>
            No daily-use number to fill in — Sensei learns the burn rate from your logged doses,
            tests, and water changes.
          </p>
        </>
      )}

      <div style={box}>
        <div className="font-heading" style={labelStyle}>
          Where you buy it (optional)
        </div>
        <input
          value={storeName}
          onChange={(e) => {
            setSaveErr('');
            setStoreName(e.target.value);
          }}
          maxLength={STORE_NAME_MAX}
          placeholder="Bulk Reef Supply, your local store…"
          style={inputStyle}
        />
      </div>
      {saveErr && (
        <p role="alert" style={{ fontSize: 14, color: 'var(--alert)', margin: '0 2px' }}>
          {saveErr}
        </p>
      )}
      <button
        type="submit"
        disabled={!canSave || busy}
        className="font-heading"
        style={{
          padding: '10px 0',
          borderRadius: 12,
          background: 'var(--cyan)',
          color: 'var(--cyan-text)',
          fontWeight: 600,
          fontSize: 13,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          opacity: !canSave || busy ? 0.5 : 1,
        }}
      >
        {isSalt && saltSize ? `Add salt (${saltSize} ${saltYield.unit})` : 'Add supply'}
      </button>
    </form>
  );
}
